import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';

const HistoryTab = ({ organization, historyData = [], loading = false }) => {
  if (!organization) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No organization selected
        </Typography>
      </Box>
    );
  }
  
  // Format date to a more readable format
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">
            Organisation History
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {loading ? (
          <Box sx={{ my: 4 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
              Loading history...
            </Typography>
          </Box>
        ) : historyData.length > 0 ? (
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {historyData.map((entry, index) => (
              <Box key={index}>
                <ListItem
                  alignItems="flex-start"
                  sx={{ 
                    px: 2, 
                    py: 1.5,
                    flexDirection: 'column',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', mb: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      minWidth: '190px'
                    }}>
                      <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(entry.date)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="500">
                        {entry.event}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    width: '100%', 
                    ml: 3.5, 
                    mt: 0.5
                  }}>
                    <Box sx={{ 
                      display: 'flex',
                      minWidth: '190px'
                    }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="500">
                        Changed by:
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2">
                        {entry.user || 'System'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {!entry.isConsolidated && (
                    <>
                      <Box sx={{ 
                        display: 'flex', 
                        width: '100%', 
                        ml: 3.5
                      }}>
                        <Box sx={{ 
                          display: 'flex',
                          minWidth: '190px'
                        }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="500">
                            From:
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2">
                            {entry.oldValue || '-'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        width: '100%', 
                        ml: 3.5
                      }}>
                        <Box sx={{ 
                          display: 'flex',
                          minWidth: '190px'
                        }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="500">
                            To:
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2">
                            {entry.newValue || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  )}
                  
                  {entry.isConsolidated && (
                    <Box sx={{ 
                      display: 'flex', 
                      width: '100%', 
                      ml: 3.5
                    }}>
                      <Box sx={{ 
                        display: 'flex',
                        minWidth: '190px'
                      }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                          Details:
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">
                          {entry.newValue || 'No details provided'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </ListItem>
                {index < historyData.length - 1 && <Divider component="li" />}
              </Box>
            ))}
          </List>
        ) : (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              backgroundColor: 'background.paper'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No history records available for this organization
            </Typography>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
};

export default HistoryTab; 